import React from 'react';
import { Card } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import {
  AlertTriangle, ChevronDown, ChevronUp, Activity, Calendar, DollarSign,
  Users, Eye, XCircle, ArrowUpRight, MessageSquare, Clock, Target,
  TrendingUp, Network, Layers, X, Trash2,
} from 'lucide-react';

// Mapeo de tipos de alerta a iconos y colores
const ALERT_TYPE_CONFIG = {
  'dispersion': { icon: Network, color: 'red', label: 'Dispersión' },
  'concentrador': { icon: Target, color: 'red', label: 'Concentrador' },
  'mismo_destino': { icon: Layers, color: 'orange', label: 'Mismo Destino' },
  'origen_externo': { icon: ArrowUpRight, color: 'orange', label: 'Origen Externo' },
  'volumen': { icon: TrendingUp, color: 'yellow', label: 'Volumen Alto' },
  'red_compartida': { icon: Network, color: 'yellow', label: 'Red Compartida' },
  'default': { icon: AlertTriangle, color: 'gray', label: 'Alerta' }
};

export const SEVERIDAD_COLORS = {
  'CRITICA': { bg: 'bg-red-100', text: 'text-red-800', border: 'border-red-300', dot: 'bg-red-500' },
  'ALTA': { bg: 'bg-orange-100', text: 'text-orange-800', border: 'border-orange-300', dot: 'bg-orange-500' },
  'MEDIA': { bg: 'bg-yellow-100', text: 'text-yellow-800', border: 'border-yellow-300', dot: 'bg-yellow-500' },
  'BAJA': { bg: 'bg-green-100', text: 'text-green-800', border: 'border-green-300', dot: 'bg-green-500' }
};

export const formatMoney = (amount) => {
  if (!amount) return '$0';
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(amount);
};

export const formatDate = (dateStr) => {
  if (!dateStr) return 'N/A';
  try {
    const date = new Date(dateStr);
    return date.toLocaleDateString('es-AR', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  } catch {
    return dateStr;
  }
};

// Card visual de una alerta individual
const AlertaCard = ({ alerta, onAction, onVerMovimientos, onDelete, isExpanded, onToggleExpand, isPendingDelete }) => {
  const severidadStyle = SEVERIDAD_COLORS[alerta.severidad] || SEVERIDAD_COLORS['MEDIA'];
  const estado = alerta.estado || 'ACTIVA';
  const isResolved = ['RESUELTA', 'FALSO_POSITIVO', 'CERRADA'].includes(estado);

  // Detectar tipo de alerta para el icono
  const tipoLower = (alerta.tipo || alerta.nombre_display || '').toLowerCase();
  let alertConfig = ALERT_TYPE_CONFIG.default;
  for (const [key, config] of Object.entries(ALERT_TYPE_CONFIG)) {
    if (tipoLower.includes(key)) {
      alertConfig = config;
      break;
    }
  }
  const IconComponent = alertConfig.icon;

  return (
    <Card className={`relative overflow-hidden transition-all duration-200 ${
      isPendingDelete ? 'opacity-40 bg-red-50 scale-95' :
      isResolved ? 'opacity-60 bg-slate-50' : 'bg-white hover:shadow-lg'
    } ${severidadStyle.border} border-l-4`}>
      {/* Botón eliminar */}
      {onDelete && !isPendingDelete && (
        <button
          onClick={(e) => {
            e.stopPropagation();
            onDelete(alerta);
          }}
          className="absolute top-2 right-2 p-1.5 rounded-full bg-white border-2 border-red-300 hover:bg-red-50 hover:border-red-500 text-red-500 hover:text-red-700 transition-colors z-10 shadow-sm"
          title="Eliminar alerta"
        >
          <X size={16} strokeWidth={2.5} />
        </button>
      )}

      {/* Indicador de eliminación pendiente */}
      {isPendingDelete && (
        <div className="absolute inset-0 flex items-center justify-center bg-red-50/80 z-10">
          <div className="text-center">
            <Trash2 size={24} className="mx-auto text-red-400 mb-1" />
            <span className="text-sm text-red-600 font-medium">Pendiente de eliminar</span>
          </div>
        </div>
      )}

      {/* Header con severidad e icono */}
      <div className="p-4">
        <div className="flex items-start justify-between mb-3">
          <div className="flex items-center gap-2">
            <div className={`p-2 rounded-lg ${severidadStyle.bg}`}>
              <IconComponent size={20} className={severidadStyle.text} />
            </div>
            <div>
              <Badge className={`${severidadStyle.bg} ${severidadStyle.text} font-semibold`}>
                {alerta.severidad}
              </Badge>
              {isResolved && (
                <Badge variant="outline" className="ml-2 bg-green-50 text-green-700 border-green-300">
                  {estado.replace('_', ' ')}
                </Badge>
              )}
              {alerta.veces_detectada > 1 && (
                <Badge
                  variant="outline"
                  className="ml-2 bg-amber-50 text-amber-700 border-amber-300"
                  title={`Detectada ${alerta.veces_detectada} veces${alerta.ultima_deteccion ? ' · última: ' + new Date(alerta.ultima_deteccion).toLocaleDateString('es-AR') : ''}`}
                  data-testid="alerta-veces-badge"
                >
                  Detectada {alerta.veces_detectada}x
                </Badge>
              )}
            </div>
          </div>

          {/* Estado indicator */}
          <div className="flex items-center gap-1 mr-6">
            <span className={`w-2 h-2 rounded-full ${severidadStyle.dot} ${!isResolved ? 'animate-pulse' : ''}`} />
          </div>
        </div>

        {/* Título y descripción */}
        <h4 className={`font-semibold text-slate-800 mb-1 ${isResolved ? 'line-through' : ''}`}>
          {alerta.tipo || alerta.nombre_display || 'Alerta Detectada'}
        </h4>
        <p className="text-sm text-slate-600 mb-3 line-clamp-2">
          {alerta.descripcion || alerta.descripcion_corta || 'Sin descripción'}
        </p>

        {/* Métricas clave */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 mb-3">
          {alerta.monto_total > 0 && (
            <div className="flex items-center gap-1 text-xs bg-slate-50 rounded px-2 py-1">
              <DollarSign size={12} className="text-green-600" />
              <span className="font-semibold text-green-700">{formatMoney(alerta.monto_total)}</span>
            </div>
          )}
          {(alerta.cantidad_movimientos || alerta.cantidad_transacciones || alerta.datos_adicionales?.total_registros) > 0 && (
            <div className="flex items-center gap-1 text-xs bg-slate-50 rounded px-2 py-1">
              <Activity size={12} className="text-blue-600" />
              <span className="font-semibold">{alerta.cantidad_movimientos || alerta.cantidad_transacciones || alerta.datos_adicionales?.total_registros} txns</span>
            </div>
          )}
          {alerta.fecha_deteccion && (
            <div className="flex items-center gap-1 text-xs bg-slate-50 rounded px-2 py-1">
              <Calendar size={12} className="text-purple-600" />
              <span>{formatDate(alerta.fecha_deteccion).split(',')[0]}</span>
            </div>
          )}
          {(alerta.datos_clave?.destinos_unicos || alerta.datos_adicionales?.destinos_unicos) > 0 && (
            <div className="flex items-center gap-1 text-xs bg-slate-50 rounded px-2 py-1">
              <Users size={12} className="text-orange-600" />
              <span>{alerta.datos_clave?.destinos_unicos || alerta.datos_adicionales?.destinos_unicos} destinos</span>
            </div>
          )}
        </div>

        {/* Período analizado */}
        {(alerta.periodo?.desde || alerta.periodo?.hasta || alerta.datos_adicionales?.fecha_desde) && (
          <div className="mb-3 p-2 bg-blue-50 rounded-lg border border-blue-100">
            <div className="flex items-center gap-2 text-xs text-blue-700">
              <Clock size={12} />
              <span className="font-medium">Período analizado:</span>
              <span>
                {formatDate(alerta.periodo?.desde || alerta.datos_adicionales?.fecha_desde)?.split(',')[0] || 'N/A'}
                {' - '}
                {formatDate(alerta.periodo?.hasta || alerta.datos_adicionales?.fecha_hasta)?.split(',')[0] || 'Actual'}
              </span>
            </div>
          </div>
        )}

        {/* Resumen de datos de la alerta — estilo unificado */}
        {(() => {
          const cantDetectada = alerta.cantidad_movimientos || alerta.cantidad_transacciones || 0;
          const cantStored = (alerta.movimientos_ids || []).length;
          const montoDetectado = parseFloat(alerta.monto_total) || 0;

          if (cantDetectada <= 0 && cantStored <= 0) return null;

          const detected = cantDetectada || cantStored;
          const isParcial = cantDetectada > 0 && cantStored > 0 && cantStored < cantDetectada;
          const isPeriodo = cantDetectada > 0 && cantStored > cantDetectada;

          return (
            <div className={`mb-3 rounded-lg px-3 py-2 border text-xs ${
              isParcial ? 'bg-amber-50 border-amber-300' : 'bg-slate-50 border-slate-200'
            }`} data-testid="alert-data-summary">
              <div className="flex items-center gap-2 flex-wrap">
                {isParcial && <AlertTriangle size={12} className="text-amber-500 shrink-0" />}
                {!isParcial && <Activity size={12} className="text-slate-400 shrink-0" />}
                <span className={isParcial ? 'font-semibold text-amber-800' : 'text-slate-700'}>
                  {isParcial
                    ? `Datos parciales: ${cantStored} de ${cantDetectada} movimientos almacenados`
                    : isPeriodo
                    ? `${detected} ops. sospechosas de ${cantStored} movimientos del período`
                    : `${detected} operaciones detectadas`
                  }
                </span>
                {montoDetectado > 0 && (
                  <span className="text-slate-500">Monto: <strong className="text-slate-700">{formatMoney(montoDetectado)}</strong></span>
                )}
                {!isParcial && !isPeriodo && cantStored === cantDetectada && cantStored > 0 && (
                  <span className="text-green-600 font-medium ml-auto">Completos</span>
                )}
              </div>
              {isParcial && (
                <p className="text-amber-600 mt-1">Usá "Cargar todas" en el detalle para ver el set completo.</p>
              )}
            </div>
          );
        })()}

        {/* Datos adicionales si existen */}
        {alerta.datos_adicionales && Object.keys(alerta.datos_adicionales).length > 0 && (
          <div className="mb-3">
            <button
              onClick={onToggleExpand}
              className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 font-medium"
            >
              {isExpanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              {isExpanded ? 'Ocultar detalles' : 'Ver detalles'}
            </button>

            {isExpanded && (
              <div className="mt-2 p-3 bg-slate-50 rounded-lg text-xs space-y-1">
                {Object.entries(alerta.datos_adicionales).slice(0, 8).map(([key, value]) => (
                  <div key={key} className="flex justify-between">
                    <span className="text-slate-500 capitalize">{key.replace(/_/g, ' ')}:</span>
                    <span className="font-medium text-slate-700">
                      {typeof value === 'number' ? value.toLocaleString() : String(value).slice(0, 30)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Notas de la alerta */}
        {alerta.notas && alerta.notas.length > 0 && (
          <div className="mb-3">
            <p className="text-xs font-semibold text-slate-600 mb-1 flex items-center gap-1">
              <MessageSquare size={12} /> Notas ({alerta.notas.length})
            </p>
            <div className="space-y-1.5 max-h-32 overflow-y-auto">
              {alerta.notas.map((nota, nIdx) => (
                <div key={nota.id || nIdx} className="p-2 bg-blue-50 rounded border border-blue-100 text-xs">
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="font-medium text-blue-800">{nota.usuario || 'Usuario'}</span>
                    <span className="text-blue-400">{nota.fecha ? new Date(nota.fecha).toLocaleDateString('es-AR') : ''}</span>
                  </div>
                  <p className="text-slate-700">{nota.comentario}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Acciones */}
        <div className="flex flex-wrap items-center gap-2 pt-3 border-t border-slate-100">
          {/* Siempre mostrar botón Ver Movimientos - usará la cuenta del cliente para filtrar */}
          <Button
            variant="outline"
            size="sm"
            className="h-7 text-xs bg-blue-50 text-blue-700 border-blue-300 hover:bg-blue-100 font-medium"
            onClick={() => onVerMovimientos(alerta)}
          >
            <Eye size={12} className="mr-1" />
            <span>{(() => {
              const stored = alerta.movimientos_ids?.length || 0;
              const detected = alerta.cantidad_movimientos || alerta.cantidad_transacciones || stored;
              if (detected > 0) return `Ver ${detected} Movs.`;
              if (stored > 0) return `Ver ${stored} Movs.`;
              return 'Ver Movs.';
            })()}</span>
          </Button>

          {!isResolved && (
            <>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs bg-orange-50 text-orange-700 border-orange-300 hover:bg-orange-100 font-medium"
                onClick={() => onAction(alerta, 'ESCALAR')}
              >
                <ArrowUpRight size={12} className="mr-1" />
                <span>Escalar</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs bg-green-50 text-green-700 border-green-300 hover:bg-green-100 font-medium"
                onClick={() => onAction(alerta, 'FALSO_POSITIVO')}
              >
                <XCircle size={12} className="mr-1" />
                <span>F. Positivo</span>
              </Button>
              <Button
                variant="outline"
                size="sm"
                className="h-7 text-xs bg-slate-50 text-slate-700 border-slate-300 hover:bg-slate-100 font-medium"
                onClick={() => onAction(alerta, 'AGREGAR_NOTA')}
              >
                <MessageSquare size={12} className="mr-1" />
                <span>Nota</span>
              </Button>
            </>
          )}
        </div>
      </div>
    </Card>
  );
};

export default AlertaCard;
